import { useEffect, useMemo, useRef, useState } from 'react'
import { Anchor, Button, Card, Group, NumberInput, Paper, Table, Text } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { Link } from 'react-router-dom'
import { Navigation } from './components'
import {
  useMergedPatients,
  useMergeSettings,
  useRestoreMergedPatient,
  useSaveMergeSettings,
  type MergeStatus,
} from '@/hooks/usePatientMerge'

/**
 * Super Admin → Merged patients (PD-1986): the retention setting and every merge, newest first, with
 * Restore for the ones the server marks `restorable`. The server enforces super admin; this page is
 * just the window. Rows load a page at a time as the sentinel scrolls into view (AuthConsole pattern).
 */

const STATE_LABEL: Record<MergeStatus['state'], string> = {
  merging: 'Merging…',
  merged: 'Merged',
  restoring: 'Restoring…',
  restored: 'Restored',
  deleted: 'Deleted',
}

/** A merge whose run failed is not "Merging…" — it waits for Resume or a roll-back. */
const stateLabel = (m: MergeStatus): string =>
  m.state === 'merging' && m.runStatus === 'failed' ? 'Merge failed' : STATE_LABEL[m.state]

const RETENTION_ERROR = 'Enter a number of days from 1 to 365'

const formatLocal = (iso: string) => new Date(iso).toLocaleString()
const formatDay = (iso: string) => new Date(iso).toLocaleDateString()

/** Invisible row that requests the next page when scrolled into view. */
function ScrollSentinel({ enabled, onVisible }: { enabled: boolean; onVisible: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined' || !ref.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) onVisible()
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [enabled, onVisible])
  if (!enabled) return null
  return <div ref={ref} className="h-1" data-testid="scroll-sentinel" />
}

/** Days a merged record is kept (and restorable) before the nightly cleanup deletes it. */
function RetentionCard() {
  const settings = useMergeSettings()
  const save = useSaveMergeSettings()
  const [days, setDays] = useState<number | string>('')
  const [error, setError] = useState<string>()
  const [saved, setSaved] = useState(false)
  const current = settings.data?.retentionDays

  useEffect(() => {
    if (current !== undefined) setDays(current)
  }, [current])

  const changed = current !== undefined && days !== current

  const submit = () => {
    if (typeof days !== 'number' || !Number.isInteger(days) || days < 1 || days > 365) {
      setError(RETENTION_ERROR)
      return
    }
    save.mutate({ retentionDays: days }, { onSuccess: () => setSaved(true) })
  }

  return (
    <Card withBorder shadow="sm" radius="md" p="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Text fw={600}>Retention</Text>
          <Text c="dimmed" size="sm">
            Days a merged record is kept before it is deleted. It can be restored until then.
          </Text>
        </div>
        <Group align="flex-start" gap="sm">
          <NumberInput
            aria-label="Retention days"
            w={130}
            allowDecimal={false}
            allowNegative={false}
            clampBehavior="none"
            suffix=" days"
            disabled={current === undefined}
            value={days}
            error={error}
            onChange={(v) => {
              setDays(v)
              setError(undefined)
              setSaved(false)
            }}
          />
          <Button disabled={!changed} loading={save.isPending} onClick={submit}>
            Save
          </Button>
          {saved && (
            <Text size="sm" c="green" mt={6}>
              Saved
            </Text>
          )}
        </Group>
      </Group>
    </Card>
  )
}

function RecordCell({ name, email }: { name: string; email?: string }) {
  return (
    <>
      <div>{name}</div>
      {email && <div className="text-xs text-gray-500">{email}</div>}
    </>
  )
}

function MergedTable({
  items,
  loading,
  error,
  onRestore,
  restoring,
}: {
  items: MergeStatus[]
  loading: boolean
  /** The list failed to load: shown with Retry instead of the empty state. */
  error?: { retrying: boolean; onRetry: () => void }
  onRestore: (merge: MergeStatus) => void
  restoring: boolean
}) {
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Merged record</Table.Th>
          <Table.Th>Kept record</Table.Th>
          <Table.Th>Merged by</Table.Th>
          <Table.Th>Merged on</Table.Th>
          <Table.Th>Deletes on</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={7} className="text-center text-sm text-gray-500">
              {loading ? (
                'Loading merged patients…'
              ) : error ? (
                <div className="flex flex-col items-center gap-2 py-2">
                  <span>Couldn&apos;t load merged patients.</span>
                  <Button
                    size="xs"
                    variant="default"
                    loading={error.retrying}
                    onClick={error.onRetry}
                  >
                    Retry
                  </Button>
                </div>
              ) : (
                'No merged patients'
              )}
            </Table.Td>
          </Table.Tr>
        )}
        {items.map((m) => {
          const { keptPatient: kept, mergedPatient: merged } = m
          return (
            <Table.Tr key={m.mergeId}>
              <Table.Td>
                <RecordCell name={merged.name} email={merged.email} />
              </Table.Td>
              <Table.Td>
                {/* A kept record that reads merged is the deleted placeholder: nothing to open. */}
                {kept.merged ? (
                  <RecordCell name={kept.name} email={kept.email} />
                ) : (
                  <>
                    <Anchor component={Link} to={`/patients/${kept.patientId}`}>
                      {kept.name}
                    </Anchor>
                    {kept.email && <div className="text-xs text-gray-500">{kept.email}</div>}
                  </>
                )}
              </Table.Td>
              <Table.Td>{m.actorName ?? '—'}</Table.Td>
              <Table.Td>{m.mergedAt ? formatLocal(m.mergedAt) : '—'}</Table.Td>
              <Table.Td>{m.deletesOn ? formatDay(m.deletesOn) : '—'}</Table.Td>
              <Table.Td>{stateLabel(m)}</Table.Td>
              <Table.Td className="text-right">
                {m.restorable && (
                  <Button
                    size="xs"
                    variant="light"
                    disabled={restoring}
                    onClick={() => onRestore(m)}
                  >
                    {m.state === 'restoring' ? 'Retry restore' : 'Restore'}
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

export function MergedPatients() {
  const list = useMergedPatients()
  const restore = useRestoreMergedPatient()
  const rows = useMemo(() => (list.data?.pages ?? []).flatMap((p) => p.data), [list.data])
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list

  const confirmRestore = (merge: MergeStatus) =>
    openConfirmModal({
      title: 'Restore merged patient',
      children: (
        <p className="text-sm">
          Restore {merge.mergedPatient.name} as a separate patient? Its login works again, and{' '}
          {merge.keptPatient.name} gives back the ids it received from it.
        </p>
      ),
      labels: { confirm: 'Restore', cancel: 'Cancel' },
      onConfirm: () => restore.mutate({ mergeId: merge.mergeId }),
    })

  return (
    <div className="p-6">
      <Navigation />
      <div className="mt-4 flex flex-col gap-4">
        <RetentionCard />
        <Paper withBorder>
          <MergedTable
            items={rows}
            loading={list.isLoading}
            error={
              list.isError
                ? { retrying: list.isFetching, onRetry: () => void list.refetch() }
                : undefined
            }
            onRestore={confirmRestore}
            restoring={restore.isPending}
          />
          <ScrollSentinel
            enabled={!!hasNextPage && !isFetchingNextPage}
            onVisible={() => void fetchNextPage()}
          />
        </Paper>
      </div>
    </div>
  )
}
